
import * as $ from 'jquery';

import * as AllyIntegrationUtil from 'src/integration/util';
import * as AllyConfig from 'src/shared/rest/Config';
import * as AllyFacade from 'src/shared/rest/facade';

// Regular expression used to detect Canvas files
var FILE_PATTERN = /\/files\/(\d+)/;

// Regular expression used to detect the Canvas course
var COURSE_PATTERN = /context-course_(\d+)/;

var fileInformationCache = {};

export function fileInformation() {
    return fileInformationCache;
}

export function getFileInformation(fileReferences, callback) {
    var fileReferencesToRetrieve = [];
    var filesToReturn = {};
    $.each(fileReferences, function(i, fileReference) {
        var fileReferenceId = fileReference.id();
        if (fileInformationCache[fileReferenceId]) {
            filesToReturn[fileReferenceId] = fileInformationCache[fileReferenceId];
        } else {
            fileReferencesToRetrieve.push(fileReference);
        }
    });

    if (fileReferencesToRetrieve.length === 0) {
        return callback(null, filesToReturn);
    }

    AllyFacade.getFileInformation(fileReferencesToRetrieve, undefined, function(err, data) {
        if (err) {
            return callback(err);
        }

        var fileInfosByReferenceId = {};
        $.each(data.uploadsReport, function(i, uploadReport) {
            var fileReference = fileReferencesToRetrieve[i];
            if (uploadReport) {
                fileInfosByReferenceId[fileReference.id()] = uploadReport;
            }
        });

        // Cache the retrieved information
        fileInformationCache = $.extend(fileInformationCache, fileInfosByReferenceId);

        // Return the information to the caller
        filesToReturn = $.extend(filesToReturn, fileInfosByReferenceId);
        var callbackData = {
            'files': filesToReturn
        };
        return callback(null, callbackData);
    });
}

/**
 * Whether the current user can manage the course. This is based on the visibility of the settings link
 * in the left hand navigation
 *
 * @return {Boolean}    Whether the current user can manage the course
 */
export function canManageCourse() {
    return ($('ul#section-tabs li.section a[href*="/settings"]').length > 0);
}

/**
 * Get the id of course the tool is currently embedded in
 *
 * @return {String}    The id of the course the tool is currently embedded in. `null` if the current context is not
 *                     a course
 */
export function getCourseId() {
    var match = $(document.body).attr('class').match(COURSE_PATTERN);
    if (match) {
        return match[1];
    } else {
        return null;
    }
}

/**
 * Get the file id from a link. The following links are supported:
 *   -  The "old way"
 *      https://ally.instructure.com/courses/9/files/2690/download?wrap=1
 *   -  The "new way"
 *      https://ally.instructure.com/files?verifier=huzTxxs7Cw4Z1ZsKrrHGsY85pKFCRyz4JFliqFVl&preview=740
 *
 * @param  {String}    link    The download link from which to extract the file id
 * @return {String}            The file id or null if no id could be found
 */
export function getFileId(link) {
    var matches = link.match(FILE_PATTERN);
    if (matches) {
        return matches[1];
    } else {
        var queryString = AllyIntegrationUtil.getQueryParameters(link);
        if (queryString.verifier && queryString.preview) {
            return queryString.preview;
        } else {
            return null;
        }
    }
}

/**
 * Get an absolute preview URL for the given file.
 *
 * @param  {Object}     fileInfo    The Ally file info object
 * @return {String}                 The absolute preview URL
 */
export function getPreviewUrl(fileInfo) {
    var previewUrl = null;
    if (fileInfo.type === 'image') {
        previewUrl = `${window.location.origin }/courses/${ AllyConfig.courseId() }/files/${ fileInfo.id
            }/preview`;
    }

    return previewUrl;
}

/**
 * Apply fixes to the given image alt text as a result of unfortunate alt text handling in Canvas. There are a
 * couple factors that this accounts for:
 *
 *  1.  It used to be that if you created an image in Canvas and left the alt text empty (default behaviour), it
 *      would just make the image "decorative" by virtue of an empty alt text attribute; and
 *  2.  Then, they "fixed" that at some point, however if you leave an empty alt attribute and then update the
 *      image, the alt tag is completely removed -- meaning if the user set the image as decorative through Ally,
 *      then it wipes away the empty alt text the next time the image is updated; and
 *  3.  Then, they have further added a proper "decorative" checkbox into image editing in the WYSIWYG editor. When
 *      it is selected, the alt attribute is properly set as the empty string and a `data-decorative="true"`
 *      attribute is set on to the content item.
 *
 * This "fix" attempts to remediate this by:
 *
 *  1.  If an image has an empty alt text, we only honour that it is marked decorative if either the
 *      `data-ally-user-updated-alt` is set to empty or the `data-decorative` attribute is set to `true
 *      (solves #1 above); and
 *  2.  If an image has no alt text, but does have the `data-ally-user-updated-alt` attribute set to the empty
 *      string, then we set the `alt` tag to the empty string on the image
 *
 * This function ensures that the alt text is set appropriately for an image.
 *
 * @param  {jQuery} $img    A jQuery object holding the `<img />` element whose alt text to fix
 */
export function fixImageAltText($img) {
    // See if we've already placed a workaround for alt
    var allyWorkAroundSourceValues = AllyIntegrationUtil.getAllyWorkAroundSourceValues($img);
    if (Object.prototype.hasOwnProperty.call(allyWorkAroundSourceValues, 'alt')) {
        // If a work-around was already applied for alt, it means that we should be able to trust it. Typical
        // scenario is that the user set the image as decorative on the central content item, we applied the
        // workaround directly in the UI, and we can trust that decorative value rather than erase it in this fix
        return;
    }

    // Determine whether or not we should trust the alt text in the HTML source, if any
    var isMarkedDataDecorative = ($img.attr('data-decorative') === 'true' || $img.attr('role') === 'presentation');
    var allyUserUpdatedAlt = AllyIntegrationUtil.getAllyUserUpdatedValue($img, 'alt');
    if ($img.attr('alt') === '' && (!isMarkedDataDecorative && allyUserUpdatedAlt !== '')) {
        // Canvas WYSIWYG at one point didn't support indicating if an image is decorative. However if the user
        // ommitted alt text from the alt text field, it would simply add an empty alt tag. Therefore if we have one
        // that is marked decorative only with the alt attribute and not with either Ally or Canvas'
        // `data-decorative` attribute or role="presentation", we remove it as it is almost always incorrect
        $img.removeAttr('alt');
    } else if (!$img.is('[alt]') && allyUserUpdatedAlt === '') {
        // Canvas at one point automatically removed the `alt` tag, even if it correctly indicated that the image is
        // decorative. So if they have set it in Ally and the attribute is missing, we know to add it back
        $img.attr('alt', '');
    }
}

/**
 * Wrap a link in a span that holds the `ally_file_link_holder` and `link_holder` classes. If the link is already
 * wrapped in a span, no action will take place.
 * This allows for scoping CSS to the correct elements.
 *
 * @param  {jQuery}     $link    The jQuery object that represents the link and that should be wrapped in a link
 *                               holder
 */
export function ensureWrappedInLinkHolder($link) {
    var linkHref = $link.attr('href');
    // Depending on what features are enabled on the client, a simple link to a course file might end up as
    // three different links: 1 for the actual file, 1 to open it in a new window and another one to open it
    // in scribd. Eventually, all links should belong to a span with a link_holder class though.
    var isWrappedInSpan = ($link.parents('span.link_holder').length > 0);
    var isCourseFile = (linkHref.indexOf('/courses/') > -1);
    if (isCourseFile && !isWrappedInSpan) {
        var html = '<span class="ally-file-link-holder link_holder">';
        html += $link.prop('outerHTML');
        html += '</span>';
        $link.replaceWith(html);
    }
}
